const SESSION_ENDPOINT = "/api/users/session";
const SESSION_BOOTSTRAP_ENDPOINT = "/api/users/session-bootstrap";
const LOGOUT_ENDPOINT = "/api/users/logout";

export type CreateServerSessionResult =
  | { status: "created" }
  | { status: "auth_error"; detail?: string }
  | { status: "unavailable" }
  | { status: "network_error" };

async function readErrorDetail(response: Response): Promise<string | undefined> {
  try {
    const body: unknown = await response.json();
    if (body && typeof body === "object" && "detail" in body) {
      const detail = (body as { detail: unknown }).detail;
      if (typeof detail === "string") return detail;
    }
  } catch {
    /* ignore non-JSON error bodies */
  }
  return undefined;
}

export async function createServerSession(
  idToken: string,
): Promise<CreateServerSessionResult> {
  try {
    const response = await fetch(SESSION_ENDPOINT, {
      method: "POST",
      credentials: "include",
      headers: { Authorization: `Bearer ${idToken}` },
    });
    if (response.ok) return { status: "created" };
    if (response.status === 401) {
      return {
        status: "auth_error",
        detail: await readErrorDetail(response),
      };
    }
    return { status: "unavailable" };
  } catch (error) {
    console.debug("[Auth] Could not create server session:", error);
    return { status: "network_error" };
  }
}

export async function hasValidServerSession(): Promise<boolean | null> {
  try {
    const response = await fetch(SESSION_ENDPOINT, {
      method: "GET",
      credentials: "include",
    });
    if (response.ok) return true;
    if (response.status === 401) return false;
    return null;
  } catch (error) {
    console.debug("[Auth] Could not check server session:", error);
    return null;
  }
}

export async function deleteServerSession(idToken?: string): Promise<void> {
  try {
    const headers: HeadersInit = {};
    if (idToken) {
      headers.Authorization = `Bearer ${idToken}`;
    }
    const response = await fetch(LOGOUT_ENDPOINT, {
      method: "POST",
      credentials: "include",
      headers,
    });
    if (!response.ok) {
      console.debug("[Auth] Server logout failed:", response.status);
    }
  } catch (error) {
    console.debug("[Auth] Server logout failed:", error);
  }
}

export async function trySessionBootstrap(): Promise<{
  firebase_token: string;
  email: string;
} | null> {
  try {
    const response = await fetch(SESSION_BOOTSTRAP_ENDPOINT, {
      method: "POST",
      credentials: "include",
    });
    if (!response.ok) return null;
    const body = (await response.json()) as {
      firebase_token?: string | null;
      email?: string | null;
    };
    if (!body.firebase_token) return null;
    return {
      firebase_token: body.firebase_token,
      email: body.email ?? "",
    };
  } catch (error) {
    console.debug("[Auth] Session bootstrap not available:", error);
  }
  return null;
}
